// hooks/useCNTranslation.ts
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { cnTranslationService } from '../services/cnTranslationService';

interface UseCNTranslationResult {
  translateCN: (cnNumber: string, defaultName: string | undefined) => string;
  isLoading: boolean;
  error: Error | null;
}

export const useCNTranslation = (): UseCNTranslationResult => {
  const { i18n } = useTranslation();
  const [translations, setTranslations] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const loadedLangRef = useRef<string>('');

  useEffect(() => {
    let mounted = true;

    const loadTranslations = async () => {
      const currentLang = i18n.language;
      
      // Skip if we already loaded this language
      if (loadedLangRef.current === currentLang) {
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        
        // Clear the service cache for fresh translations
        cnTranslationService.clearCache(currentLang);
        
        // Get all CN codes from the data (we'll need to load them all)
        // For now, we'll trigger loading when translateCN is called
        await cnTranslationService.preloadTranslations(currentLang);
        
        if (mounted) {
          loadedLangRef.current = currentLang;
          setTranslations({}); // Clear old translations
          setIsLoading(false);
        }
      } catch (err) {
        if (mounted) {
          setError(err as Error);
          setIsLoading(false);
        }
      }
    };

    loadTranslations();

    return () => {
      mounted = false;
    };
  }, [i18n.language]);

  const translateCN = (cnNumber: string, defaultName: string | undefined): string => {
    if (!defaultName) {
      return '';
    }

    // Check if we have this translation cached
    const cachedTranslation = translations[cnNumber];
    if (cachedTranslation) {
      return cachedTranslation;
    }

    // If not cached, load it asynchronously and update state
    cnTranslationService.getTranslatedName(cnNumber, defaultName, i18n.language)
      .then((translated) => {
        if (translated !== defaultName) {
          setTranslations(prev => ({
            ...prev,
            [cnNumber]: translated
          }));
        }
      })
      .catch(err => {
        console.error(`Failed to translate CN ${cnNumber}:`, err);
      });

    // Return default name while loading
    return defaultName;
  };

  return { translateCN, isLoading, error };
};